import type {ReactNode} from 'react';

import {Avatar} from '../../avatar/avatar';
import {Card} from '../../card/card';
import {colors} from '../../colors/colors';
import {LoadingText} from '../../loading-placeholder/loading-text';
import {Metric} from '../../metrics/metric';
import type {PopoverInfoBodyColor} from '../../popover-info/popover-info';
import {ProgressBar} from '../../progress-bar/progress-bar';

import styles from './stat-card.module.css';

interface UserStatProps {
    id: string;
    name: string;
    initials: string;
    avatarUrl: string;
}

interface Props {
    type: 'text' | 'percent' | 'limit';
    displayName: string | null | undefined;
    value: string | null | undefined;
    isLoading?: boolean;
    isPrimary?: boolean;
    user?: UserStatProps;
    tooltipInfo?: string | ReactNode;
    tooltipBodyColor?: PopoverInfoBodyColor;
    limit?: number | null;
    isUnlimited?: boolean;
    showProgressBars?: boolean;
    progressBarColors?: {
        backgroundColor: string | undefined;
        barColor: string | undefined;
    };
}

export function StatCard(props: Props) {
    const {showProgressBars = true} = props;

    const progressBarColors = {
        backgroundColor: props.progressBarColors?.backgroundColor ?? colors.aiPurplePinkLt,
        barColor: props.progressBarColors?.barColor ?? colors.aiPurplePrimary,
    };

    const formatValue = () => {
        if (props.type === 'percent') {
            return `${props.value}%`;
        }
        if (props.type === 'limit') {
            const limitDisplay = props.isUnlimited ? '∞' : props.limit?.toLocaleString() ?? '∞';
            return `${Number(props.value).toLocaleString()} / ${limitDisplay}`;
        }
        const numValue = Number(props.value);
        if (props.value?.trim() !== '' && !isNaN(numValue)) {
            return numValue.toLocaleString();
        }
        return props.value ?? '';
    };

    const renderBottomRow = () => {
        if (props.type === 'percent' && props.value && showProgressBars) {
            return (
                <div className='pt-16'>
                    <ProgressBar
                        percentComplete={parseInt(props.value) / 100}
                        backgroundColor={progressBarColors.backgroundColor}
                        color={progressBarColors.barColor}
                        height={4}
                    />
                </div>
            );
        }
        if (
            props.type === 'limit' &&
            !props.isUnlimited &&
            props.limit &&
            props.value &&
            showProgressBars
        ) {
            const percentComplete = Math.min(Number(props.value) / props.limit, 1);
            return (
                <div className='pt-16'>
                    <ProgressBar
                        percentComplete={percentComplete}
                        backgroundColor={progressBarColors.backgroundColor}
                        color={progressBarColors.barColor}
                        height={4}
                    />
                </div>
            );
        }
        return undefined;
    };

    return (
        <Card
            hasBorder={true}
            hasShadow={true}
            borderRadius={8}
            fitHeight={true}
            variant={props.isPrimary ? 'ai-animated' : 'default'}
        >
            <div className={styles.container}>
                {props.isLoading ? (
                    <>
                        <LoadingText height={17} width={150} color={colors.offWhiteDk} />
                        <LoadingText height={17} width={80} color={colors.offWhiteDk} />
                    </>
                ) : (
                    <>
                        {props.displayName && props.value !== undefined && props.value !== null && (
                            <Metric
                                title={props.displayName}
                                value={formatValue()}
                                isCentered={false}
                                size='normal'
                                statIsBold={true}
                                tooltipInfo={props.tooltipInfo}
                                tooltipBodyColor={props.tooltipBodyColor}
                                bottomRow={renderBottomRow()}
                                valueIcon={
                                    props.user ? (
                                        <div className='mt-2'>
                                            <Avatar
                                                avatarUrl={props.user.avatarUrl}
                                                initials={props.user.initials}
                                                type='users'
                                                size={24}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            />
                        )}
                    </>
                )}
            </div>
        </Card>
    );
}
